import {
    DEFAULT_SITE_ICON,
    ANDROID_PLATFORM,
    DEV_STATUS_IN_PRODUCTION,
    DEV_STATUS_IN_TESTING,
    DEVELOPMENT_STATUS_VALUES,
    IOS_PLATFORM,
    NEW_APP_ID,
    OS_LIST,
    OTHER_PLATFORM,
    SINGULAR_DEFAULT_SITE_ICON,
    WEB_PLATFORM,
} from './constants';

const asLower = value => String(value || '').toLowerCase();
const FAKE_PASSWORD = '********';
const SECONDS_TO_HOUR_FACTOR = 3600;
const KEYWORD_MIN_LENGTH = 4;
const KEYWORD_MAX_LENGTH = 500;
const STORE_SELECTION_PLATFORM_NAMES = [ANDROID_PLATFORM, IOS_PLATFORM, WEB_PLATFORM, OTHER_PLATFORM];
const MANUAL_PUBLIC_ID_PLATFORM_NAMES = OS_LIST.filter(platform => !STORE_SELECTION_PLATFORM_NAMES.includes(platform));

const interpolateTranslation = (template, data = null) => {
    if (typeof template !== 'string' || !data) {
        return template;
    }

    return Object.entries(data).reduce((resolvedTemplate, [key, value]) => {
        const placeholderPattern = new RegExp(`{{\\s*${key}\\s*}}`, 'g');

        return resolvedTemplate.replace(placeholderPattern, value ?? '');
    }, template);
};

export const normalizeTranslatedValue = value => {
    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        return value.find(item => typeof item === 'string' && item) || '';
    }

    return '';
};

export const translateText = (translate, id, data = null) => {
    const translated = normalizeTranslatedValue(typeof translate === 'function' ? translate(id, data) : id);

    if (translated && translated !== id) {
        return normalizeTranslatedValue(interpolateTranslation(translated, data));
    }

    return normalizeTranslatedValue(interpolateTranslation(translated, data));
};

export const compareAppsByName = (a, b) => {
    if (a.id === 'new_app_id') return -1;
    if (b.id === 'new_app_id') return 1;
    return asLower(a.name).localeCompare(asLower(b.name));
};

export const ensureSiteDestinations = site => ({
    regular: site.destinations?.regular || [],
    deeplink: site.destinations?.deeplink || [],
    ios_universal_link: site.destinations?.ios_universal_link || [],
});

const sanitizeDestinations = destinations => ensureSiteDestinations({ destinations: destinations || {} });

const cleanDestinations = destinations => {
    const normalizedDestinations = sanitizeDestinations(destinations);
    return Object.keys(normalizedDestinations).reduce(
        (cleanedDestinations, destinationType) => ({
            ...cleanedDestinations,
            [destinationType]: normalizedDestinations[destinationType]
                .map(destination => ({
                    ...destination,
                    url: (destination.url || '').trim(),
                }))
                .filter(destination => destination.url),
        }),
        {}
    );
};

const getPlatformByName = (platformName, platforms = []) => platforms.find(platform => platform.name === platformName);

export const getPlatformName = siteOrPlatform => {
    const platform = siteOrPlatform?.platform || siteOrPlatform;
    const os = siteOrPlatform?.os;
    if (os?.name) {
        return os.name;
    }
    if (platform?.simplified?.name) {
        return platform.simplified.name;
    }
    if (platform?.name) {
        return platform.name;
    }
    return platform || OTHER_PLATFORM;
};

const getCanonicalOsForPlatform = (platform, platforms = []) => {
    const platformName = platform?.name || platform;
    const simplifiedName = platform?.simplified?.name || platformName;

    if (OS_LIST.includes(platformName)) {
        return getPlatformByName(platformName, platforms) || platform;
    }

    if (OS_LIST.includes(simplifiedName)) {
        return (
            getPlatformByName(simplifiedName, platforms) || {
                name: simplifiedName,
                display_name: platform?.simplified?.display_name || simplifiedName,
            }
        );
    }

    return getPlatformByName(OTHER_PLATFORM, platforms) || { name: OTHER_PLATFORM, display_name: 'Other' };
};

export const inferSitePublished = site => {
    const platformName = site.platform?.name || site.platform;
    const osName = site.os?.name || getPlatformName(site);

    if (!site.id) {
        return STORE_SELECTION_PLATFORM_NAMES.includes(platformName);
    }

    if (
        [IOS_PLATFORM, ANDROID_PLATFORM].includes(osName) &&
        site.longname &&
        site.url &&
        !/^https?:\/\//.test(site.url)
    ) {
        return false;
    }

    return !MANUAL_PUBLIC_ID_PLATFORM_NAMES.includes(platformName);
};

export const normalizeSite = (site, platforms = []) => {
    const platform =
        typeof site.platform === 'string'
            ? getPlatformByName(site.platform, platforms) || { name: site.platform, display_name: site.platform }
            : site.platform;
    const os = site.os
        ? typeof site.os === 'string'
            ? getPlatformByName(site.os, platforms) || { name: site.os, display_name: site.os }
            : site.os
        : getCanonicalOsForPlatform(platform, platforms);
    const normalizedSite = {
        ...site,
        platform,
        os,
        destinations: ensureSiteDestinations(site),
        development_status: site.development_status || DEV_STATUS_IN_PRODUCTION,
        fcm_server_key: site.fcm_server_key_configured ? FAKE_PASSWORD : site.fcm_server_key,
        ios_push_certificate_p12_key: site.ios_push_certificate_p12_key_configured
            ? FAKE_PASSWORD
            : site.ios_push_certificate_p12_key,
        _local_ios_push_certificate_file:
            site._local_ios_push_certificate_file ||
            (site.ios_push_certificate_p12_filename
                ? { name: site.ios_push_certificate_p12_filename, fromServer: true }
                : site._local_ios_push_certificate_file),
        initial_session_dedup_window:
            site.initial_session_dedup_window === undefined
                ? site.session_dedup_window
                : site.initial_session_dedup_window,
        initial_inactivity_window:
            site.initial_inactivity_window === undefined ? site.inactivity_window_h : site.initial_inactivity_window,
        show_advanced_settings: !!site.show_advanced_settings,
    };

    return {
        ...normalizedSite,
        _published: inferSitePublished(normalizedSite),
    };
};

export const normalizeApp = (app, platforms = []) => ({
    ...app,
    formName: app.formName || app.name || '',
    sites: (app.sites || [])
        .map(site => normalizeSite(site, platforms))
        .sort((a, b) => asLower(a.platform?.name || a.platform).localeCompare(asLower(b.platform?.name || b.platform))),
});

export const normalizeApps = (apps = [], platforms = []) =>
    apps.map(app => normalizeApp(app, platforms)).sort(compareAppsByName);

export const siteMatchesSearch = (site, search) => {
    if (!search) {
        return true;
    }
    return [site.url, site.longname, site.alias, site.public_id].some(value => asLower(value).includes(search));
};

export const appMatchesSearch = (app, searchText = '') => {
    const search = asLower(searchText).trim();
    if (!search) {
        return true;
    }
    return asLower(app.name).includes(search) || app.sites.some(site => siteMatchesSearch(site, search));
};

export const filterApps = ({
    apps = [],
    searchText = '',
    developmentStatus = DEV_STATUS_IN_PRODUCTION,
    showStoreLiveOnly = false,
} = {}) =>
    apps
        .filter(app => app.id === NEW_APP_ID || appMatchesSearch(app, searchText))
        .map(app => ({
            ...app,
            visibleSites: app.sites.filter(site => {
                if (app.id === NEW_APP_ID) {
                    return true;
                }

                const matchesStatus = site.development_status === developmentStatus;
                const matchesLiveOnly = !showStoreLiveOnly || !site.id || site.is_live_in_store;
                return matchesStatus && matchesLiveOnly;
            }),
        }))
        .filter(app => app.id === NEW_APP_ID || app.visibleSites.length > 0);

export const getAppCountByStatus = (apps = []) =>
    apps.reduce((counts, app) => {
        const statuses = new Set(app.sites.map(site => site.development_status).filter(Boolean));
        statuses.forEach(status => {
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    }, {});

export const getAvailableDevelopmentStatuses = (apps = []) =>
    DEVELOPMENT_STATUS_VALUES.reduce(
        (available, status) => ({
            ...available,
            [status]: apps.some(app => app.sites.some(site => site.development_status === status)),
        }),
        {}
    );

export const getSiteIcon = app => {
    if (app?.icon && app.icon !== DEFAULT_SITE_ICON && app.icon !== SINGULAR_DEFAULT_SITE_ICON) {
        return app.icon;
    }

    const siteWithIcon = (app.sites || []).find(site => site.icon && site.icon !== SINGULAR_DEFAULT_SITE_ICON);
    return siteWithIcon?.icon || DEFAULT_SITE_ICON;
};

export const getPublicIdForDisplay = (publicId, defaultValue = '-') => {
    if (!publicId || /.*\$.+-internal$/.test(publicId)) {
        return defaultValue;
    }
    return publicId;
};

export const getStatusLabel = status => (status === DEV_STATUS_IN_TESTING ? 'Test Apps' : 'Live Apps');

export const getPlatformFamilyName = siteOrPlatform => {
    const platform = siteOrPlatform?.platform || siteOrPlatform;

    if (platform?.simplified?.name) {
        return platform.simplified.name;
    }

    return getPlatformName(siteOrPlatform);
};

export const getPlatformDisplayName = siteOrPlatform => {
    const platform = siteOrPlatform?.platform || siteOrPlatform;
    const os = siteOrPlatform?.os;
    const name = getPlatformName(siteOrPlatform);

    if (os?.display_name) return os.display_name;
    if (platform?.display_name) return platform.display_name;

    const displayNames = {
        [ANDROID_PLATFORM]: 'Android',
        [IOS_PLATFORM]: 'iOS',
        [WEB_PLATFORM]: 'Web',
        metaquest: 'MetaQuest',
        nintendo: 'Nintendo',
        pc: 'PC',
        ctv: 'CTV',
        xbox: 'Xbox',
        playstation: 'Playstation',
        [OTHER_PLATFORM]: 'Other',
    };

    return displayNames[name] || name;
};

export const getCanonicalPlatformNames = (platforms = []) => {
    if (!platforms.length) {
        return OS_LIST;
    }

    const availablePlatformNames = new Set(
        platforms.map(platform => getPlatformName(platform)).filter(platformName => OS_LIST.includes(platformName))
    );

    return OS_LIST.filter(platformName => availablePlatformNames.has(platformName));
};

export const getPlatformOption = (platformName, platforms = []) => {
    const existingPlatform = platforms.find(
        platform => getPlatformName(platform) === platformName || platform.name === platformName
    );
    return existingPlatform || { name: platformName, display_name: getPlatformDisplayName(platformName) };
};

export const isIosOrAndroid = site => [IOS_PLATFORM, ANDROID_PLATFORM].includes(getPlatformFamilyName(site));

export const isNonGamingPlatform = site =>
    [IOS_PLATFORM, ANDROID_PLATFORM, WEB_PLATFORM, OTHER_PLATFORM].includes(getPlatformFamilyName(site));

export const isSiteReadyToSave = site => {
    if (!getPlatformName(site)) return false;
    if (site._published === false) return !!site.longname;
    return !!site.url;
};

export const isValidPackageIdentifier = site => {
    const longname = site?.longname;
    const osName = site?.os?.name || getPlatformName(site);

    if (!longname) return false;

    if ([OTHER_PLATFORM, WEB_PLATFORM].includes(osName)) {
        if (
            /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
                longname
            )
        ) {
            return true;
        }
    }

    return /^([A-Za-z\d_-]+\.?)+$/.test(longname);
};

export const validIosStoreUrlPublicId = url => /\/id(\d+)$|\/id(\d+)\?/.test(url || '');

export const extractStorePublicId = (url, platformName) => {
    const value = url || '';

    if (platformName === ANDROID_PLATFORM) {
        const androidMatch = value.match(/[?&]id=([^&#]+)/);
        return androidMatch ? decodeURIComponent(androidMatch[1]) : '';
    }

    if (platformName === IOS_PLATFORM) {
        const iosMatch = value.match(/\/id(\d+)(?:[/?#]|$)/);
        return iosMatch ? iosMatch[1] : '';
    }

    return '';
};

const getSitePlatformIdentity = site => site?.platform?.name || site?.platform || getPlatformName(site);

const isSameSite = (site, siteId) => !!siteId && site.id === siteId;

export const isValidStorePublicId = (publicId, siteId, platform, apps = []) => {
    if (!publicId) return true;
    const platformName = getSitePlatformIdentity(platform);

    return apps.every(app =>
        (app.sites || []).every(site => {
            if (!site?.id || isSameSite(site, siteId)) {
                return true;
            }

            return !(site.public_id === publicId && getSitePlatformIdentity(site) === platformName);
        })
    );
};

export const isValidLongname = (longname, platform, siteId, apps = []) => {
    if (!longname) return true;
    const platformName = getSitePlatformIdentity(platform);

    return apps.every(app =>
        (app.sites || []).every(site => {
            if (!site?.id || isSameSite(site, siteId)) {
                return true;
            }

            return !(site.longname === longname && getSitePlatformIdentity(site) === platformName);
        })
    );
};

export const validateSiteForSave = ({
    apps = [],
    site,
    adminMode = false,
    userData = {},
    showRequired = true,
} = {}) => {
    const errors = {};
    const osName = site?.os?.name || getPlatformName(site);
    const isPublished = site?._published !== false;
    const publicId = site?.public_id || extractStorePublicId(site?.url, osName);

    if (showRequired && isPublished) {
        if (!site?.url) {
            errors.url = 'Field cannot be empty';
        }
    } else if (showRequired && !site?.longname) {
        errors.longname = 'Field cannot be empty';
    }

    if (!errors.url && publicId && !isValidStorePublicId(publicId, site.id, site.platform, apps)) {
        errors.url = 'A site with the same store id already exists';
    }

    if (!errors.longname && site?.longname) {
        if (!isValidPackageIdentifier(site)) {
            errors.longname = 'Invalid bundle identifier';
        } else if (!isValidLongname(site.longname, site.platform, site.id, apps)) {
            errors.longname = 'A site with the same bundle identifier already exists';
        }
    }

    const keyword = (site?.keyword || '').trim();
    if (keyword && (keyword.length < KEYWORD_MIN_LENGTH || keyword.length > KEYWORD_MAX_LENGTH)) {
        errors.keyword = `Mapping Key must be between ${KEYWORD_MIN_LENGTH} and ${KEYWORD_MAX_LENGTH} characters`;
    }

    if (
        isPublished &&
        !site?.id &&
        site?.storeDetectedPlatformName &&
        site.storeDetectedPlatformName !== site?.platform?.simplified?.name &&
        site.storeDetectedPlatformName !== site?.platform?.name &&
        [IOS_PLATFORM, WEB_PLATFORM, ANDROID_PLATFORM].includes(osName)
    ) {
        errors.url = 'The site URL does not match the selected OS';
    }

    if (
        site?.id &&
        site?.sdk_integrated &&
        !!(userData?.attribution_enabled || userData?.sdk_enabled) &&
        site.initial_session_dedup_window !== undefined &&
        site.initial_session_dedup_window !== null &&
        site.session_dedup_window !== site.initial_session_dedup_window &&
        !adminMode &&
        userData?.should_limit_session_dedup_window &&
        site.session_dedup_window < SECONDS_TO_HOUR_FACTOR
    ) {
        errors.session_dedup_window =
            'Your account does not support a Minimum Session Interval lower than one hour.\nContact Singular support for more information.';
    }

    if (osName === IOS_PLATFORM && isPublished && !validIosStoreUrlPublicId(site?.url)) {
        errors.url = 'Invalid public id in Apple store url';
    }

    return errors;
};

export const buildSiteSavePayload = site => {
    const osName = site.os?.name || getPlatformName(site);
    const payload = {
        ...site,
        alias: site.alias || '',
        keyword: site.keyword || '',
        attribution_app_id: site.attribution_app_id || '',
        ...(site.destinations ? { destinations: cleanDestinations(site.destinations) } : {}),
        platform: !site.os || osName === OTHER_PLATFORM ? site.platform?.name || getPlatformName(site) : osName,
        lds_default: typeof site.lds_default === 'object' ? site.lds_default?.name : site.lds_default,
        admon_revenue_source:
            typeof site.admon_revenue_source === 'object' ? site.admon_revenue_source?.name : site.admon_revenue_source,
        new_admon_revenue_source:
            typeof site.new_admon_revenue_source === 'object'
                ? site.new_admon_revenue_source?.name
                : site.new_admon_revenue_source,
    };

    if (payload._published === false) {
        payload.icon = payload.icon || DEFAULT_SITE_ICON;
        payload.url =
            osName === OTHER_PLATFORM || getPlatformFamilyName(site) === OTHER_PLATFORM
                ? payload.url || payload.longname
                : payload.longname;
    }

    if (!payload.ios_universal_link_subdomain && payload._local_ios_universal_link_subdomain) {
        payload.ios_universal_link_subdomain = payload._local_ios_universal_link_subdomain;
    }

    delete payload.os;
    delete payload.show_advanced_settings;
    delete payload.collapsed;
    delete payload.isSaving;
    delete payload.isLoadingStoreDetails;
    delete payload.isTestingFcmIntegration;
    delete payload.fcmIntegrationMessage;
    delete payload.saveError;
    delete payload.longnameDuplicationDetails;
    delete payload.validationErrors;
    delete payload.new_admon_dialog_open;
    delete payload.storeDetectedPlatformName;
    delete payload._clientKey;
    delete payload._local_ios_universal_link_subdomain;
    delete payload.fcm_server_key_configured;
    delete payload.ios_push_certificate_p12_key_configured;
    delete payload.latest_sdk_version;
    delete payload.sdk_version;

    if (payload.icon === DEFAULT_SITE_ICON) {
        delete payload.icon;
    }

    return payload;
};
